import { memo, useCallback, useMemo } from 'react';

import { PlusIcon, SquareLibraryIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';

import { LibraryCreateModal } from '~/components/LibraryBeta/LibraryCreateModal/LibraryCreateModal';
import { SubSideNavSectionHeader } from '~/components/v2/components/workspace/sub-side-nav/SubSideNavSectionHeader';
import { WorkspaceSidebarLibrariesMoreDropdown } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesMoreDropdown';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateLibrary } from '~/utils/permissions/libraryPermissions';

export const WorkspaceSidebarLibrariesHeader = memo(() => {
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const [showLibraryCreateModal] = useAirModal(LibraryCreateModal);
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const canCreateLibraries = canCreateLibrary(workspacePermissions);

  const handleCreateLibrary = useCallback(() => {
    showLibraryCreateModal();
  }, [showLibraryCreateModal]);

  const actions = useMemo(
    () => (
      <div className="flex items-center gap-1">
        <WorkspaceSidebarLibrariesMoreDropdown />
        {canCreateLibraries && (
          <IconButton
            adsIcon
            appearance="ghost"
            color="gray"
            label="Create new library"
            onClick={handleCreateLibrary}
            size="small"
            icon={PlusIcon}
          />
        )}
      </div>
    ),
    [canCreateLibraries, handleCreateLibrary],
  );

  return (
    <SubSideNavSectionHeader
      actions={actions}
      alwaysShowActions
      collapsed={collapsed}
      data-testid="LIBRARIES_SUB_SIDE_NAV_LIBRARIES_HEADER"
      icon={SquareLibraryIcon}
      label="Libraries"
      onToggle={toggleCollapsed}
    />
  );
});

WorkspaceSidebarLibrariesHeader.displayName = 'WorkspaceSidebarLibrariesHeader';
